import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import api from '../../services/api';
import { showError } from '../../services/alert';

const hoje = new Date();
const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().slice(0, 10);
const hojeIso = hoje.toISOString().slice(0, 10);

const ControleFinanceiroAgro = () => {
  const navigate = useNavigate();

  const [filtros, setFiltros] = useState({
    dataInicio: primeiroDiaMes,
    dataFim: hojeIso,
    safraId: '',
    talhaoId: '',
    colheitaId: '',
    somentePagos: true
  });
  const [resumo, setResumo] = useState(null);
  const [safras, setSafras] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [colheitas, setColheitas] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    carregarApoio();
  }, []);

  const carregarApoio = async () => {
    try {
      const [safrasRes, talhoesRes, colheitasRes] = await Promise.all([
        api.get('/safras'),
        api.get('/talhoes'),
        api.get('/Colheita')
      ]);
      setSafras(safrasRes.data || []);
      setTalhoes(talhoesRes.data || []);
      setColheitas(colheitasRes.data || []);
    } catch {
      showError('Não foi possível carregar filtros do agro.');
    }
  };

  const carregarResumo = async () => {
    setLoading(true);
    try {
      const params = {
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim,
        somentePagos: filtros.somentePagos
      };
      if (filtros.safraId) params.safraId = filtros.safraId;
      if (filtros.talhaoId) params.talhaoId = filtros.talhaoId;
      if (filtros.colheitaId) params.colheitaId = filtros.colheitaId;

      const res = await api.get('/financeiro/agro/fechamento-caixa', { params });
      setResumo(res.data);
    } catch {
      showError('Erro ao carregar controle financeiro agro.');
      setResumo(null);
    } finally {
      setLoading(false);
    }
  };

  const moeda = (valor) =>
    Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const dataPt = (valor) => valor ? new Date(valor).toLocaleDateString('pt-BR') : '';

  const atualizarFiltro = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const grupos = [
    ['Por forma de pagamento', resumo?.porFormaPagamento],
    ['Por safra', resumo?.porSafra],
    ['Por talhão', resumo?.porTalhao],
    ['Por colheita', resumo?.porColheita]
  ];

  return (
    <div className="container mt-4">
      <Header />

      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-light btn-sm d-flex align-items-center gap-1"
              onClick={() => navigate('/menu')}
              disabled={loading}
            >
              <i className="bi bi-arrow-left text-success"></i>
              <span className="text-success fw-semibold">Voltar</span>
            </button>

            <div>
              <h5 className="mb-0">Controle financeiro agro</h5>
              <small className="text-white-50">Fechamento de caixa, livro caixa, lucro e pendencias fiscais</small>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="row g-3 align-items-end mb-4">
            <div className="col-md-2">
              <label className="form-label fw-semibold">Inicio</label>
              <input className="form-control" type="date" value={filtros.dataInicio} onChange={e => atualizarFiltro('dataInicio', e.target.value)} />
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">Fim</label>
              <input className="form-control" type="date" value={filtros.dataFim} onChange={e => atualizarFiltro('dataFim', e.target.value)} />
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">Safra</label>
              <select className="form-select" value={filtros.safraId} onChange={e => atualizarFiltro('safraId', e.target.value)}>
                <option value="">Todas</option>
                {safras.map(item => <option key={item.id} value={item.id}>{item.cultura}</option>)}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">Talhão</label>
              <select className="form-select" value={filtros.talhaoId} onChange={e => atualizarFiltro('talhaoId', e.target.value)}>
                <option value="">Todos</option>
                {talhoes.map(item => <option key={item.id} value={item.id}>{item.nome}</option>)}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">Colheita</label>
              <select className="form-select" value={filtros.colheitaId} onChange={e => atualizarFiltro('colheitaId', e.target.value)}>
                <option value="">Todas</option>
                {colheitas.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.produtoColhido || 'Colheita'} - {dataPt(item.data)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <div className="form-check mb-2">
                <input
                  id="somentePagos"
                  className="form-check-input"
                  type="checkbox"
                  checked={filtros.somentePagos}
                  onChange={e => atualizarFiltro('somentePagos', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="somentePagos">Somente pagos</label>
              </div>
              <button className="btn btn-success w-100" onClick={carregarResumo} disabled={loading}>
                {loading ? 'Carregando...' : 'Atualizar'}
              </button>
            </div>
          </div>

          {!resumo && (
            <div className="alert alert-info mb-0">
              Informe o período e atualize para ver caixa, livro caixa e resultado.
            </div>
          )}

          {resumo && (
            <>
              <div className="row g-3 mb-4">
                {[
                  ['Saldo anterior', resumo.saldoAnterior, 'text-secondary'],
                  ['Entradas', resumo.totalEntradas, 'text-success'],
                  ['Saídas', resumo.totalSaidas, 'text-danger'],
                  ['Saldo final', resumo.saldoFinal, Number(resumo.saldoFinal || 0) >= 0 ? 'text-success' : 'text-danger']
                ].map(([label, value, color]) => (
                  <div className="col-md-3" key={label}>
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <small className="text-muted">{label}</small>
                        <h4 className={`${color} mb-0`}>{moeda(value)}</h4>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {!!resumo.pendencias?.length && (
                <div className="alert alert-warning">
                  <strong>Pendencias de controle:</strong>
                  <ul className="mb-0 mt-2">
                    {resumo.pendencias.slice(0, 12).map((item, index) => (
                      <li key={`${item.tipo}-${item.lancamentoId}-${index}`}>
                        {item.tipo} #{item.lancamentoId}: {item.mensagem}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="row g-3 mb-4">
                {grupos.map(([titulo, itens]) => (
                  <div className="col-md-6" key={titulo}>
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-header bg-light fw-semibold">{titulo}</div>
                      <div className="table-responsive">
                        <table className="table table-sm mb-0">
                          <tbody>
                            {(itens || []).slice(0, 6).map(item => (
                              <tr key={item.chave}>
                                <td>{item.chave}</td>
                                <td className="text-end text-success">{moeda(item.entradas)}</td>
                                <td className="text-end text-danger">{moeda(item.saidas)}</td>
                                <td className={`text-end ${Number(item.resultado || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                                  {moeda(item.resultado)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light fw-semibold">Livro caixa</div>
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Tipo</th>
                        <th>Descrição</th>
                        <th>Safra</th>
                        <th>Talhão</th>
                        <th>Documento</th>
                        <th className="text-end">Entrada</th>
                        <th className="text-end">Saida</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(resumo.livroCaixa || []).map(item => (
                        <tr key={`${item.tipo}-${item.lancamentoId}`}>
                          <td>{dataPt(item.data)}</td>
                          <td>{item.tipo}</td>
                          <td>{item.descricao}</td>
                          <td>{item.safra || '-'}</td>
                          <td>{item.talhao || '-'}</td>
                          <td>{item.chaveNotaFiscal || item.chaveCte || '-'}</td>
                          <td className="text-end text-success">{item.entrada ? moeda(item.entrada) : '-'}</td>
                          <td className="text-end text-danger">{item.saida ? moeda(item.saida) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ControleFinanceiroAgro;
